/**
 * indexBaselineDiff.ts
 * Item 5.4 — pure diff logic for the Phase-2 ("Index tests") tab's
 * Compare-to-Baseline overlay (I16: "Compare-to-Baseline ghosting covers
 * index allocations").
 *
 * Two independent diffs, both computed from data already fetched by the
 * existing endpoints (no new backend work — per the item's requirements):
 *
 *   1. Row diff — per-TaskType: this source's DRAFT `IndexAllocation` vs its
 *      BASELINE-scenario-keyed counterpart (`listIndexAllocations('BASELINE',
 *      { sourceType, sourceKey })`). A TaskType with no baseline row is
 *      treated as "new since baseline" (both fields flagged changed) — there
 *      is nothing to ghost against, so the row simply reads as new.
 *
 *   2. Day-cell ghost — per calendar date: the BASELINE perspective's
 *      `firmPct`/`isOverCapacity` (`getIndexCapacity(from, to)` — omitting
 *      `scenarioKey` mirrors Phase-1's `fetchMachineBookings` no-scenarioKey
 *      baseline fetch: Committed rows classify as firm regardless of
 *      ScenarioKey — see `indexCapacityService.classifyRow` — so this is the
 *      "world without this draft's own tentative contribution") next to the
 *      draft's own day model already built by `indexHeatmapModel.ts`.
 *
 * Neither function recomputes spread/utilization math — both perspectives'
 * numbers are backend truth (or, for rows, the already-fetched allocation
 * fields); this module only aligns two lists by key and reports what
 * changed. Bulk actions are explicitly out of scope for this item (I16) —
 * nothing here touches multi-row selection/editing.
 */

import type { IndexDayCapacity } from '../../../../../services/scheduleIndex';

// ---------------------------------------------------------------------------
// Row diff (per TaskType)
// ---------------------------------------------------------------------------

/** The subset of `IndexAllocation` fields the row diff needs. */
export interface BaselineDiffAllocationInput {
  TaskTypeKey: string;
  TestCount: number;
  StartDate: string;
  EndDate: string;
}

export interface RowBaselineDiff {
  /** false when this TaskType has no row at the BASELINE scenario key (new since baseline). */
  hasBaseline: boolean;
  baselineTestCount: number | null;
  baselineStartDate: string | null;
  baselineEndDate: string | null;
  /** true when TestCount differs from baseline, or there is no baseline row to compare. */
  testCountChanged: boolean;
  /** true when Start or End differs from baseline, or there is no baseline row to compare. */
  windowChanged: boolean;
}

/** Index baseline allocations by TaskTypeKey (one active row per TaskType per source, I17). */
export function buildBaselineIndexByTaskType(
  baselineAllocations: readonly BaselineDiffAllocationInput[],
): Map<string, BaselineDiffAllocationInput> {
  const map = new Map<string, BaselineDiffAllocationInput>();
  for (const b of baselineAllocations) map.set(b.TaskTypeKey, b);
  return map;
}

/** Diff one draft row against its baseline counterpart (or "no baseline" when absent). */
export function diffRowAgainstBaseline(
  draft: BaselineDiffAllocationInput,
  baselineByTaskType: ReadonlyMap<string, BaselineDiffAllocationInput>,
): RowBaselineDiff {
  const baseline = baselineByTaskType.get(draft.TaskTypeKey) ?? null;
  if (!baseline) {
    return {
      hasBaseline: false,
      baselineTestCount: null,
      baselineStartDate: null,
      baselineEndDate: null,
      testCountChanged: true,
      windowChanged: true,
    };
  }
  return {
    hasBaseline: true,
    baselineTestCount: baseline.TestCount,
    baselineStartDate: baseline.StartDate,
    baselineEndDate: baseline.EndDate,
    testCountChanged: baseline.TestCount !== draft.TestCount,
    windowChanged: baseline.StartDate !== draft.StartDate || baseline.EndDate !== draft.EndDate,
  };
}

/** Build the full draft-TaskTypeKey → RowBaselineDiff map for the Phase-2 table. */
export function buildRowBaselineDiffMap(
  draftAllocations: readonly BaselineDiffAllocationInput[],
  baselineAllocations: readonly BaselineDiffAllocationInput[],
): Map<string, RowBaselineDiff> {
  const baselineByTaskType = buildBaselineIndexByTaskType(baselineAllocations);
  const map = new Map<string, RowBaselineDiff>();
  for (const draft of draftAllocations) {
    map.set(draft.TaskTypeKey, diffRowAgainstBaseline(draft, baselineByTaskType));
  }
  return map;
}

// ---------------------------------------------------------------------------
// Day-cell ghost (heatmap)
// ---------------------------------------------------------------------------

export interface DayBaselineGhost {
  date: string;
  /** BASELINE perspective's firmPct for this day (Committed, any scenario + BASELINE tentative). */
  baselineFirmPct: number;
  baselineIsOverCapacity: boolean;
  /** Own (draft-perspective) firmPct − baselineFirmPct — what this plan itself adds/removes. */
  deltaPct: number;
}

/**
 * Pair the draft's own day models with the BASELINE-perspective day capacity
 * by date. Days present in one list but not the other (a mismatched horizon —
 * shouldn't normally happen since both fetches share the same [from, to])
 * are simply skipped rather than guessed at.
 */
export function buildDayBaselineGhostMap(
  ownDays: readonly Pick<IndexDayCapacity, 'date' | 'firmPct'>[],
  baselineDays: readonly IndexDayCapacity[],
): Map<string, DayBaselineGhost> {
  const baselineByDate = new Map(baselineDays.map((d) => [d.date, d]));
  const map = new Map<string, DayBaselineGhost>();
  for (const own of ownDays) {
    const base = baselineByDate.get(own.date);
    if (!base) continue;
    map.set(own.date, {
      date: own.date,
      baselineFirmPct: base.firmPct,
      baselineIsOverCapacity: base.isOverCapacity,
      deltaPct: Math.round((own.firmPct - base.firmPct) * 10) / 10,
    });
  }
  return map;
}

/** Key for the per-type ghost map (P3): `${taskTypeKey}|${date}`. */
export function typeGhostKey(taskTypeKey: string, date: string): string {
  return `${taskTypeKey}|${date}`;
}

/**
 * P3 (2026-07-29) — per-type analogue of `buildDayBaselineGhostMap`: pairs each
 * per-type row cell with the BASELINE perspective's per-type firmPct for the
 * same (type, date). Keyed by `typeGhostKey`. Types/dates absent from the
 * baseline response ghost against 0% (the type simply doesn't run in baseline).
 */
export function buildTypeBaselineGhostMap(
  ownRows: readonly {
    taskTypeKey: string;
    cells: readonly { date: string; firmPct: number }[];
  }[],
  baselineDays: readonly IndexDayCapacity[],
): Map<string, DayBaselineGhost> {
  const baselineByDate = new Map(baselineDays.map((d) => [d.date, d]));
  const map = new Map<string, DayBaselineGhost>();
  for (const row of ownRows) {
    for (const cell of row.cells) {
      const base = baselineByDate.get(cell.date);
      if (!base) continue;
      const baseEntry = base.byType.find((b) => b.taskTypeKey === row.taskTypeKey) ?? null;
      const baselineFirmPct = baseEntry?.firmPct ?? 0;
      map.set(typeGhostKey(row.taskTypeKey, cell.date), {
        date: cell.date,
        baselineFirmPct,
        baselineIsOverCapacity: baseEntry?.isOverCapacity ?? false,
        deltaPct: Math.round((cell.firmPct - baselineFirmPct) * 10) / 10,
      });
    }
  }
  return map;
}

/** True when comparing to baseline is meaningful — only for a still-editable draft (I16 gate). */
export function isCompareToBaselineEligible(scenarioStatus: string | undefined | null): boolean {
  return scenarioStatus === 'Draft' || scenarioStatus === 'NeedsReview';
}
